/**
 * Shared error helpers for openapi-fetch results. Several endpoints in the
 * generated client don't declare error responses, so the typed `error` is
 * `never`. We still need to handle non-2xx at runtime — Response carries that.
 */

import { DomainError, type ApiError } from '@leanin/shared/errors';

/**
 * Unwrap an openapi-fetch result for a body-returning endpoint: returns the
 * success payload, or throws a `DomainError` carrying the server's error code
 * AND the HTTP status (via `response`), so a gateway timeout surfaces as
 * `http_504` and the retry policy can tell a permanent 4xx from a 5xx.
 *
 * Use at openapi-fetch mutation/query sites instead of the hand-rolled
 * `if (error !== undefined || data === undefined) throwApiError(error, response)`.
 */
export function unwrap<T>(result: {
  data?: T;
  error?: unknown;
  response: Response;
}): T {
  if (result.data === undefined || result.error !== undefined) {
    throwApiError(result.error, result.response);
  }
  return result.data;
}

/**
 * Companion to `unwrap` for no-content (204) endpoints that return no body on
 * success: throws (with status) on any non-2xx, returns void otherwise.
 */
export function unwrapVoid(result: {
  error?: unknown;
  response: Response;
}): void {
  if (!result.response.ok) {
    throwApiError(result.error, result.response);
  }
}

/**
 * A `DomainError` thrown by `throwApiError` when the caller passed the
 * `response`, so the HTTP status is known. The server's code envelope alone
 * can't be mapped back to a status without duplicating the server's
 * code→status table, and the retry policy (`query-retry.ts`) needs the status
 * to tell a permanent rejection (422) from a transient one (429, 5xx).
 */
export class HttpApiError extends DomainError {
  readonly status: number;
  constructor(error: ApiError, status: number) {
    super(error);
    this.status = status;
    Object.setPrototypeOf(this, HttpApiError.prototype);
    this.name = 'HttpApiError';
  }
}

/**
 * The two "this resource isn't there" statuses. 404 is never-existed or
 * not-visible-to-you; 410 (`GONE`) is public content that has since been
 * deleted — the public resource and learning-track endpoints return it in
 * place of 404 so crawlers can drop the URL. Both must land on the branded
 * not-found surface, never the generic "Couldn't load …" retry surface.
 */
export type NotFoundStatus = 404 | 410;

/**
 * Marker error thrown by `throwApiError` when the underlying response was
 * one of `NotFoundStatus`. Detail-page routes test for this via
 * `isNotFoundApiError(err)` and re-throw `notFound()` from
 * `@tanstack/react-router` so the branded 404 page renders instead of the
 * generic "Couldn't load …" surface. `status` carries which of the two it
 * was, for callers that need to distinguish gone from missing.
 */
export class NotFoundApiError extends HttpApiError {
  declare readonly status: NotFoundStatus;
  constructor(code: string, status: NotFoundStatus) {
    super({ code } as ApiError, status);
    Object.setPrototypeOf(this, NotFoundApiError.prototype);
    this.name = 'NotFoundApiError';
  }
}

export function isNotFoundApiError(err: unknown): err is NotFoundApiError {
  return err instanceof NotFoundApiError;
}

/**
 * The request never got an HTTP answer: `fetch` itself rejected (offline, DNS,
 * a dropped connection, a request the browser or a crawler's robots rules
 * refused) or the API client's request timeout cut it off. `authFetch` throws it in place of the platform's bare `TypeError`,
 * which a bug in a query function also throws, so the two stay apart.
 */
export class NetworkRequestError extends Error {
  constructor(cause: unknown) {
    super('Network request failed', { cause });
    Object.setPrototypeOf(this, NetworkRequestError.prototype);
    this.name = 'NetworkRequestError';
  }
}

/**
 * A request that failed for a reason outside the page: no HTTP answer, a 5xx,
 * a 408 or a 429. Narrower than `failureKind(err) === 'transient'`, which also
 * covers errors without a status (a parse failure, a bug in a query function)
 * because it cannot prove those permanent.
 */
export function isTransientRequestFailure(err: unknown): boolean {
  if (err instanceof NetworkRequestError) return true;
  return err instanceof HttpApiError && failureKind(err) === 'transient';
}

/**
 * Single typed entry point for narrowing openapi-fetch `error` payloads to
 * the server's `code` string. The wire shape is `{ error: { code, message,
 * details? } }`; centralising the assertion here means an envelope-shape
 * evolution only touches this module.
 */
export function parseApiErrorCode(error: unknown): string | null {
  const inner = (error as { error?: { code?: unknown } } | undefined)?.error;
  if (inner === null || inner === undefined) return null;
  return typeof inner.code === 'string' ? inner.code : null;
}

/**
 * Why a request failed, as far as the caller can act on it. Drives both the
 * query retry policy (`query-retry.ts`, only `transient` is retried) and the
 * load-failure copy (`LoadErrorState`), so the two can never disagree.
 *
 * Only a 4xx is the server's definitive answer to the request. Anything else —
 * a network throw (no status), a 5xx, or a 2xx whose body didn't parse — is
 * `transient`: we can't prove the failure is permanent. A bare 401 is
 * `signedOut`, not transient — the client attaches a fresh Clerk token to
 * every request and the server reports an unreachable Clerk as 503, so
 * re-sending can't succeed.
 */
export type FailureKind =
  | 'transient'
  | 'invalid'
  | 'denied'
  | 'signedOut'
  | 'notFound';

export function failureKind(err: unknown): FailureKind {
  if (!(err instanceof HttpApiError)) return 'transient';
  const { status } = err;
  if (status === 404 || status === 410) return 'notFound';
  if (status === 401) return 'signedOut';
  if (status === 403) return 'denied';
  if (status === 408 || status === 429) return 'transient';
  return status >= 400 && status < 500 ? 'invalid' : 'transient';
}

/**
 * The machine code of any thrown/returned API failure: the wire envelope
 * (`{ error: { code } }`, which a `DomainError` from `throwApiError` also
 * carries) wins, then a top-level `{ code }` (Clerk runtime errors, SSE
 * error frames, hand-built `{ code }` values). `null` when there is none.
 */
export function apiErrorCode(err: unknown): string | null {
  const nested = parseApiErrorCode(err);
  if (nested !== null) return nested;
  if (err !== null && typeof err === 'object') {
    const code = (err as { code?: unknown }).code;
    if (typeof code === 'string') return code;
  }
  return null;
}

/**
 * Companion to `parseApiErrorCode` for routes that need the validation
 * `details` envelope (per-field arrays). Returns `null` when the field is
 * absent or shape-mismatched.
 */
export function parseApiErrorDetails(
  error: unknown,
): Record<string, ReadonlyArray<string>> | null {
  const inner = (error as { error?: { details?: unknown } } | undefined)?.error;
  if (inner === null || inner === undefined) return null;
  const details = inner.details;
  if (details === null || typeof details !== 'object') return null;
  return details as Record<string, ReadonlyArray<string>>;
}

/**
 * Adapt a VALIDATION envelope's per-field `details` into the mutable
 * `Record<string, string[]>` shape form components hold in their
 * `fieldErrors` state, so a server-side rejection lands on the field that
 * caused it instead of only in the form-level banner. Returns `null` when
 * the error carries no per-field detail.
 */
export function fieldErrorsFromApiError(
  err: unknown,
): Record<string, string[]> | null {
  const details = parseApiErrorDetails(err);
  if (details === null) return null;
  const entries: Array<[string, string[]]> = Object.entries(details).map(
    ([field, messages]) => [field, [...messages]],
  );
  return entries.length > 0 ? Object.fromEntries(entries) : null;
}

/**
 * Re-throws a failed openapi-fetch result as an `HttpApiError` carrying the
 * server's `code` from the body envelope and the HTTP status. Prefer `unwrap`
 * / `unwrapVoid`; call this directly only when a call site must branch on the
 * status first (e.g. map a 404 to `null`).
 *
 * `response` is required: openapi-fetch always has one by the time it returns
 * an `error`, and the status is what the retry policy and `LoadErrorState`
 * classify on. A 404 or 410 becomes the `NotFoundApiError` specialization,
 * which detail-page routes (`/members/{id}`, `/circles/{id}`, …) check for to
 * render the branded NotFoundPage instead of a generic retry surface.
 */
export function throwApiError(error: unknown, response: Response): never {
  // When the body carries our `{ error: { code } }` envelope, that code wins.
  // Otherwise the response came from infrastructure (gateway 502/504, body not
  // JSON) — fall back to `http_<status>` so the failure is triageable.
  const { status } = response;
  const code = parseApiErrorCode(error) ?? `http_${status}`;
  if (status === 404 || status === 410) {
    throw new NotFoundApiError(code, status);
  }
  // Re-throw the server's full error object (code + details), not just the
  // code — form surfaces read the VALIDATION per-field `details` from it.
  const inner = (error as { error?: unknown } | undefined)?.error;
  const apiError =
    inner !== null && typeof inner === 'object' && 'code' in inner
      ? (inner as ApiError)
      : ({ code } as ApiError);
  throw new HttpApiError(apiError, status);
}
